import { Button } from '@almbrand/button';
import { Grid, GridCell } from '@almbrand/grid';
import { Headline } from '@almbrand/headline';
import { Paragraph } from '@almbrand/paragraph';
import { SearchField } from '@almbrand/searchfield';
import { ThemeNames } from 'constantsValues/index';
import { Total } from 'generated-api/bff/api';
import { useThemeName } from 'hooks/useThemeName';
import React from 'react';
import styles from './PolicyOverviewHeader.module.scss';

interface PolicyOverviewHeaderProps {
	title: string;
	total: Total | null;
	shouldShowStatusMessage: boolean;
	isLoading: boolean;
	setSearchTerm: (value: string) => void;
	handleDownloadOverview: () => void;
}

export const PolicyOverviewHeader: React.FC<PolicyOverviewHeaderProps> = ({
	title,
	total,
	shouldShowStatusMessage,
	isLoading,
	setSearchTerm,
	handleDownloadOverview,
}) => {
	const themeName = useThemeName();
	const isAlmbrand = themeName === ThemeNames.almbrand;
	const isCodan = themeName === ThemeNames.codan;

	let subHeadlineText: string;
	if (isAlmbrand && isLoading) {
		subHeadlineText = 'Indlæser...';
	} else if (isCodan) {
		subHeadlineText = `Her kan du se et samlet overblik over dine forsikringer.`;
	} else {
		subHeadlineText = `Din samlede pris ${total?.amount?.toLocaleString('da-DK') || '0'} kr./år`;
	}

	const subHeadlineParagraphType = isCodan
		? ({ type: 'standfirst', kind: 'title' } as const)
		: ({ kind: 'paragraph', size: 'default' } as const);

	return (
		<>
			<Grid
				className={isAlmbrand ? 'pb-spacing-md-r' : 'pb-spacing-r'}
				container
			>
				{/* Left Section: Headline */}
				<GridCell
					mobileWidth='12col'
					tabletWidth={isCodan ? '6col' : '12col'}
					desktopWidth='6col'
					nogutter
					className={styles.PolicyOverviewHeader}
				>
					<Headline
						title={title}
						size={isAlmbrand ? 'default' : 'sm'}
						variant='h1'
						fontColor={isAlmbrand ? 'primaryDark' : 'primary'}
						className={styles.PolicyOverviewHeader__headline}
					/>

					<Paragraph
						text={subHeadlineText}
						fontColor='primaryDark'
						paragraphType={subHeadlineParagraphType}
						className={styles.PolicyOverviewHeader__subHeadline}
					/>
				</GridCell>

				{/* Right Section: Search Input */}
				<GridCell
					mobileWidth='12col'
					tabletWidth={isCodan ? '6col' : '12col'}
					desktopWidth='6col'
					nogutter
					className={styles.PolicyOverviewHeader__searchContainer}
				>
					<div className={styles.PolicyOverviewHeader__searchRow}>
						<SearchField
							themeName={themeName}
							placeholder={'Søg i alle forsikringer'}
							onChange={(e) => setSearchTerm(e.target.value)}
							className={styles.PolicyOverviewHeader__searchField}
						/>

						{isAlmbrand && (
							<>
								<div className={styles.PolicyOverviewHeader__separator} />
								<Button
									buttonType='downloadLink'
									size='large'
									onClick={handleDownloadOverview}
									className={styles.PolicyOverviewHeader__downloadBtn}
									iconProps={{
										themeName: themeName,
										icon: 'download',
										width: '24px',
										height: '24px',
									}}
								>
									Download overblik
								</Button>
							</>
						)}
					</div>
				</GridCell>
			</Grid>

			{/* Download overview button in its own grid below the headline/search grid */}
			{isCodan && !shouldShowStatusMessage && (
				<Grid
					container
					verticalAligment='center'
				>
					{/* <GridCell
						mobileWidth='12col'
						tabletWidth='6col'
						desktopWidth='6col'
						nogutter
					>
						<Button
							buttonType='tertiary'
							size='small'
							onClick={handleDownloadOverview}
							className={styles.PolicyOverviewHeader__downloadBtn}
							iconProps={{ themeName: themeName, icon: 'download', width: '24px', height: '24px' }}
							iconPosition='right'
						>
							Download overblik
						</Button>
					</GridCell> */}

					<GridCell
						mobileWidth='12col'
						tabletWidth='6col'
						desktopWidth='6col'
						nogutter
						// textAlign={'right'}
					>
						<Paragraph
							paragraphType={{
								kind: 'paragraph',
								size: 'sm',
							}}
							text={`Samlet pris i alt`}
							fontColor='secondaryDarkGrey'
						/>

						<Paragraph
							paragraphType={{
								kind: 'paragraph',
								size: 'default',
							}}
							fontColor='primary'
							text={`${total?.amount?.toLocaleString('da-DK') || '0'} kr./ år`}
						/>
					</GridCell>
				</Grid>
			)}
		</>
	);
};
